import {
  createPriorityOfFulfillments,
  getCurrentlyAvailable,
  updateAvailableFulfillments,
  getFulfillmentLocation,
  getFulfillmentMethodFromCart,
  FULFILLMENT_METHODS,
  ABBREVIATED_FULFILLMENT_METHODS,
} from './promo-fulfillment-utils';

/**
 * @typedef {Object} CartRequest
 * @property {Array} itemDetails - The itemDetails
 * @property {string} localStoreId - The localStoreId
 */

/**
 * @typedef {Object} CartRequestWrapper
 * @property {CartRequest} CartRequest - The CartRequest
 */

/**
 * @typedef {Object} CartRequestBuilderOptions
 * @property {Object} product - The product model
 * @property {Object} promoItemModel - The mcc-cart product itemModel
 * @property {number} desiredQuantity - The desired ***final*** quantity
 * @property {Object} store - The store object
 * @property {string} selectedFulfillment - The preferred fulfillment method
 */

const emptyPromoCart = {
  promoCartValue: 0,
  promoCartQuantity: 0,
  promoCartItemsObj: {},
  promoSuccessLevel: '',
  freeItemInCart: null,
  currentPromoTier: 0,
};

const cartIsEmpty = (rawCartModel) => rawCartModel?.messagesModel?.[0]?.shortDescription === 'Empty Shopping Cart';
const isValidRawCart = (CartModel) => {
  return !!CartModel && (!!CartModel.itemModels || cartIsEmpty(CartModel));
};

/**
 *  TODO: need to differentiate fetch from update
 * fetch can successfully return itemModels alongside an errorModel
 * some of the itemModels are good, others may be associated with the cart error
 * errorModel messages will have a correlationId to associate it with an itemModel
 * what do we do with this info? so many edge cases. sigh...
 * Seems that any update request for a single item, if it has an error, it did not change quantity in cart
 * need to check above for BOGO multi add though
 */
const cartResponseHandler = (rawCartResponse) => {
  const CartModel = rawCartResponse?.CartModel;

  if (!isValidRawCart(CartModel)) {
    const cartError = CartModel?.errorModel?.[0] || {};
    const errorMessage = new Error(`cart response contains error: ${cartError.errorCode} ${cartError.description}`);
    /* eslint-disable-next-line no-console */
    console.error(errorMessage);

    return Promise.reject(errorMessage);
  }

  return Promise.resolve(CartModel);
};

const cartFunctions = {
  fetchCartHelper: () => {
    if (typeof window === 'undefined') return Promise.reject();

    return window.THDCart.getCartData()
      .then(cartResponseHandler);
  },
  updateCartHelper: (CartRequest) => {
    if (typeof window === 'undefined') return Promise.reject();

    return window.THDCart.updateCartData({ CartRequest })
      .then(cartResponseHandler);
  },
  addToCartHelper: (CartRequest) => {
    if (typeof window === 'undefined') return Promise.reject();

    return window.THDCart.addToCart(CartRequest)
      .then(cartResponseHandler);
  },
};

const getHost = () => {
  let host = 'https://www.homedepot.com';
  if (typeof window !== 'undefined') {
    const origin = window.location.origin;
    host = origin.match(/local/g) ? 'https://www.homedepot.com' : origin;
  }
  return host;
};

const generateAddToCartItemDetail = ({
  itemId, fulfillmentMethod, store, quantity, displayPosition
}) => {
  const fulfillmentLocation = getFulfillmentLocation({ fulfillmentMethod, store });

  return {
    fulfillmentMethod,
    fulfillmentLocation,
    quantity,
    itemId,
    displayPosition,
  };
};

const buildAnalyticsCartEventObject = (cartRequest, cartResponse) => {
  const { localStoreId: storeId } = cartRequest.CartRequest;
  const cartEvent = {
    cartId: cartResponse.orderId || '',
    items: cartRequest.CartRequest.itemDetails.map(({ itemId, quantity, fulfillmentMethod }) => {
      return ({
        productInfo: {
          sku: itemId,
        },
        quantity,
        price: {
          basePrice: (cartResponse?.itemModels || []).find((item) => item.itemId === itemId)?.unitPrice || '',
        },
        fulfillment: {
          method: ABBREVIATED_FULFILLMENT_METHODS[fulfillmentMethod],
          store: fulfillmentMethod === FULFILLMENT_METHODS.BOPIS ? storeId : '',
          zip: '',
        }
      });
    })
  };
  return cartEvent;
};

/**
 * Builds a CartRequest for mcc-cart
 * @param {CartRequestBuilderOptions} options - CartRequest builder options
 * @returns {CartRequestWrapper} an object containing CartRequest
 */
const buildAddToCartRequest = ({
  product, promoItemModel, desiredQuantity = 1, store, selectedFulfillment = ''
}) => {
  const { itemId, displayPosition = '' } = product;
  const fulfillmentPriorities = createPriorityOfFulfillments(selectedFulfillment);
  const currentlyAvailable = getCurrentlyAvailable(promoItemModel, product);
  let neededToFulfill = desiredQuantity;

  const itemDetails = fulfillmentPriorities
    .reduce((items, fulfillmentMethod) => {
      const availableQty = currentlyAvailable[fulfillmentMethod] || 0;
      const quantity = Math.min(neededToFulfill, availableQty);

      if (quantity > 0) {
        neededToFulfill -= quantity;
        items.push(generateAddToCartItemDetail({
          itemId, fulfillmentMethod, store, quantity, displayPosition,
        }));
      }

      return items;
    }, []);

  return { CartRequest: { itemDetails, localStoreId: store.storeId } };
};

const buildAddToCartOverlayOptions = (channel, overrides = {}) => {
  return {
    channel,
    hidden: true,
    misship: false,
    paypal: false,
    host: getHost(),
    ...overrides
  };
};

const buildUpdateCartRequest = ({
  product, promoItemModel, desiredQuantity, store, selectedFulfillment = ''
}) => {
  let itemDetails = [];
  let neededToFulfill = desiredQuantity - (promoItemModel?.quantity || 0);
  const fulfillmentPriorities = createPriorityOfFulfillments(selectedFulfillment);

  if (neededToFulfill > 0) {
    let currentlyAvailable = getCurrentlyAvailable(promoItemModel, product);

    // Updating or adding quantities to cart
    itemDetails = fulfillmentPriorities.reduce((items, fulfillmentMethod) => {
      const availableQty = currentlyAvailable[fulfillmentMethod];
      if (!availableQty || neededToFulfill <= 0) return items;

      // Updating item
      const lineItem = promoItemModel.fulfillments[fulfillmentMethod];
      if (lineItem && lineItem.id && lineItem.quantity) {
        const qtyChange = Math.min(neededToFulfill, availableQty);
        neededToFulfill -= qtyChange;
        currentlyAvailable = updateAvailableFulfillments(fulfillmentMethod, currentlyAvailable, qtyChange);

        items.push({
          itemId: promoItemModel.itemId,
          quantity: qtyChange + lineItem.quantity,
          lineItemId: lineItem.id,
          fulfillmentMethod,
          fulfillmentLocation: getFulfillmentLocation({ fulfillmentMethod, store })
        });
      }

      return items;
    }, []);

  } else if (neededToFulfill < 0) {
    // reduceRight will subtract values from low-pri to high-pri fulfillments
    itemDetails = fulfillmentPriorities.reduceRight((items, fulfillmentMethod) => {
      if (neededToFulfill >= 0) return items;

      const lineItem = promoItemModel.fulfillments[fulfillmentMethod];
      if (lineItem && lineItem.id && lineItem.quantity) {
        const quantity = Math.max(neededToFulfill + lineItem.quantity, 0);

        if (quantity < lineItem.quantity) {
          neededToFulfill -= quantity - lineItem.quantity;
          items.push({
            itemId: promoItemModel.itemId,
            quantity,
            lineItemId: lineItem.id,
            fulfillmentMethod,
            fulfillmentLocation: getFulfillmentLocation({ fulfillmentMethod, store })
          });
        }
      }

      return items;
    }, []);

  }

  return { CartRequest: { itemDetails, localStoreId: store.storeId } };
};

const getPromoSuccessLevel = ({
  isBogo,
  isMsb,
  isBmsm,
  freeItemInCart,
  hasAnchorItemInCart,
  bogoSelectedProduct,
  promoCartValue,
  promoCartQuantity,
  progressMade,
  minPurchaseQuantity,
  minPurchaseAmount,
}) => {
  if (isBogo) {
    if (freeItemInCart && hasAnchorItemInCart) return 'success';
    if (bogoSelectedProduct) return 'almost';
  }

  if (isMsb) {
    if (!promoCartQuantity || !promoCartValue) return '';
    let amountLeft;
    let percentLeft;
    if (minPurchaseQuantity) {
      amountLeft = Math.max(0, minPurchaseQuantity - promoCartQuantity);
      percentLeft = Math.max(0, Math.floor(100 - (promoCartQuantity / minPurchaseQuantity) * 100));
    } else { // minPurchaseAmount
      amountLeft = Math.max(0, minPurchaseAmount - promoCartValue);
      percentLeft = Math.max(0, Math.floor(100 - (promoCartValue / minPurchaseAmount) * 100));
    }
    if (amountLeft === 0 || percentLeft === 0) return 'success';
    if (amountLeft === 1 || percentLeft <= 10) return 'almost';

  }

  if (isBmsm && progressMade === 100) return 'success';

  return '';
};

const cartItemIsSanitary = (cartItem) => {
  return cartItem
    && cartItem.itemId
    && typeof cartItem.lineItemId === 'string'
    && cartItem.lineItemId.length
    && Number(cartItem.quantity)
    && Number(cartItem.totalItemPrice)
    && Array.isArray(cartItem.fulfillmentModel)
    && cartItem.fulfillmentModel[0]
    && cartItem.fulfillmentModel[0].fulfillmentMethod;
};

const generatePromoCartItemsObj = ({ itemModels, allFulfillableProductsAndAnchor }) => {
  const promoCartItemsObj = {};

  if (!Array.isArray(itemModels)) return promoCartItemsObj;

  itemModels
    .filter((cartItem) => cartItemIsSanitary(cartItem) && allFulfillableProductsAndAnchor.has(cartItem.itemId))
    .forEach((promoCartItemEntry) => {
      const image = promoCartItemEntry.image;
      const itemId = promoCartItemEntry.itemId;
      const quantity = Number(promoCartItemEntry.quantity);
      const actualItemPrice = Number(allFulfillableProductsAndAnchor.get(itemId).pricing?.value);
      // grab pricing from product
      // if pricing is unavailable, use default mechanism for calculation
      // TODO: eventually remove fallback
      const totalItemPrice = actualItemPrice
        ? Number(actualItemPrice * quantity)
        : Number(promoCartItemEntry.totalItemPrice);
      const id = promoCartItemEntry.lineItemId;
      const newFulfillmentMethod = getFulfillmentMethodFromCart(promoCartItemEntry);

      if (!promoCartItemsObj[itemId]) {
        promoCartItemsObj[itemId] = {
          itemId,
          image,
          quantity,
          totalItemPrice,
          fulfillments: {
            [newFulfillmentMethod]: {
              quantity,
              id
            }
          }
        };
      } else {
        promoCartItemsObj[itemId].quantity += quantity;
        promoCartItemsObj[itemId].totalItemPrice += totalItemPrice;
        promoCartItemsObj[itemId].fulfillments[newFulfillmentMethod] = { quantity, id };
      }
    });

  return promoCartItemsObj;
};

// Gets the first tier that hasn't been completed for bmsm. If all tiers have been completed returns the last tier.
// Returns 0 for non bmsm promotions
// Tiers are 0 based
const getCurrentPromoTier = (isBmsm, tiers, promoCartValue, promoCartQuantity) => {
  if (!isBmsm) return 0;

  const currentIndex = tiers.findIndex(({ minPurchaseAmount, minPurchaseQuantity }) => {
    if (minPurchaseAmount) return promoCartValue < minPurchaseAmount;
    if (minPurchaseQuantity) return promoCartQuantity < minPurchaseQuantity;
    return false;
  });

  return currentIndex === -1 ? tiers.length - 1 : currentIndex;
};

const getProgressMade = ({ promoCartQuantity, promoCartValue, minPurchaseAmount, minPurchaseQuantity }) => {
  const progressMadeQuantity = Math.floor((promoCartQuantity / minPurchaseQuantity) * 100) || 0;
  const progressMadeAmount = Math.floor((promoCartValue / minPurchaseAmount) * 100) || 0;
  const progressMade = Math.min(100, minPurchaseQuantity ? progressMadeQuantity : progressMadeAmount);

  return progressMade;
};

// Returns progress made into the current tier.
const getMultiTierPromoProgress = ({ promoCartValue, promoCartQuantity, rewardTiers, currentPromoTier }) => {
  const {
    minPurchaseAmount: currentTierMinAmount,
    minPurchaseQuantity: currentTierMinQuantity
  } = rewardTiers[currentPromoTier];

  if (currentPromoTier === 0) {
    return getProgressMade({
      promoCartValue,
      promoCartQuantity,
      minPurchaseAmount: currentTierMinAmount,
      minPurchaseQuantity: currentTierMinQuantity
    });
  }

  const {
    minPurchaseAmount: previousTierMinAmount,
    minPurchaseQuantity: previousTierMinQuantity
  } = rewardTiers[currentPromoTier - 1];

  // Progress made from previous tiers need to be removed in order to calculate current tier progress
  const minPurchaseAmount = currentTierMinAmount - previousTierMinAmount;
  const minPurchaseQuantity = currentTierMinQuantity - previousTierMinQuantity;

  const currentTierQuantity = promoCartQuantity - previousTierMinQuantity;
  const currentTierValue = promoCartValue - previousTierMinAmount;

  const progressMade = getProgressMade({
    promoCartQuantity: currentTierQuantity,
    promoCartValue: currentTierValue,
    minPurchaseQuantity,
    minPurchaseAmount,
  });

  return progressMade;
};

const getPromotionProgress = ({
  rewardTiers, currentPromoTier, promoCartValue, promoCartQuantity, minPurchaseAmount, minPurchaseQuantity
}) => {
  const progressMade = rewardTiers.length > 1
    ? getMultiTierPromoProgress({ promoCartValue, promoCartQuantity, rewardTiers, currentPromoTier })
    : getProgressMade({ promoCartValue, promoCartQuantity, minPurchaseAmount, minPurchaseQuantity });

  const isPromoAchieved = progressMade === 100;
  return [progressMade, isPromoAchieved];
};

const transformCartModelToPromoCart = ({
  rawCartModel = {},
  isBogo = false,
  isMsb = false,
  isBmsm = false,
  displayableItemIds = [],
  anchorItemId = '',
  bogoSelectedProduct = '',
  rewardTiers = [],
  allFulfillableProductsAndAnchor
} = {}) => {
  const { itemModels } = rawCartModel;

  const promoCartItemsObj = generatePromoCartItemsObj({ itemModels, allFulfillableProductsAndAnchor });

  const hasAnchorItemInCart = !!promoCartItemsObj?.[anchorItemId]?.quantity;

  let promoCartValue = 0;
  let promoCartQuantity = 0;
  Object.values(promoCartItemsObj)
    .forEach((promoItem) => {
      promoCartValue += promoItem.totalItemPrice;
      promoCartQuantity += promoItem.quantity;
    });

  const currentPromoTier = getCurrentPromoTier(isBmsm, rewardTiers, promoCartValue, promoCartQuantity);

  const { minPurchaseAmount, minPurchaseQuantity } = rewardTiers[currentPromoTier] || {};

  const [progressMade, isPromoAchieved] = getPromotionProgress({
    rewardTiers,
    currentPromoTier,
    promoCartValue,
    promoCartQuantity,
    minPurchaseAmount,
    minPurchaseQuantity
  });

  const nonAnchorIdInCart = (displayableItemIds || []).find((itemId) => promoCartItemsObj[itemId]);
  const freeItemInCart = (isBogo && nonAnchorIdInCart) ? promoCartItemsObj[nonAnchorIdInCart] : null;

  const promoSuccessLevel = getPromoSuccessLevel({
    isMsb,
    isBogo,
    isBmsm,
    freeItemInCart,
    hasAnchorItemInCart,
    bogoSelectedProduct,
    promoCartValue,
    promoCartQuantity,
    progressMade,
    rewardTiers,
    minPurchaseAmount,
    minPurchaseQuantity,
  });

  return {
    promoCartItemsObj,
    promoCartValue,
    promoCartQuantity,
    progressMade,
    promoSuccessLevel,
    isPromoAchieved,
    freeItemInCart: promoSuccessLevel === 'success' ? freeItemInCart : null,
    currentPromoTier
  };
};

const updateCartCountInHeader = (newCartModel) => {
  // Need to emit event for cart count in header. IE Safe.
  const cartEvent = document.createEvent('CustomEvent');
  cartEvent.initCustomEvent('cartUpdated', true, false, { detail: { CartModel: newCartModel } });
  document.dispatchEvent(cartEvent);
};

export {
  emptyPromoCart,
  isValidRawCart,
  cartFunctions,
  transformCartModelToPromoCart,
  buildUpdateCartRequest,
  buildAddToCartRequest,
  buildAddToCartOverlayOptions,
  generatePromoCartItemsObj,
  getHost,
  updateCartCountInHeader,
  buildAnalyticsCartEventObject,
};
